import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ForgeButton, IconButton } from '@/components/forge/ForgeButton'
import { ForgeSelect } from '@/components/forge/ForgeSelect'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { PlaceFilter } from '@/components/forge/PlaceFilter'
import { PlainBadge } from '@/components/forge/StatusBadge'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import { PAGE_SIZES, TablePagination } from '@/components/forge/TablePagination'
import { Mono, Num } from '@/components/forge/primitives'
import { usePermission } from '@/features/auth/permissions'
import { useConnections, useConnectorTypes } from '@/features/dataFusion/queries'
import { usePlaceNodes } from '@/features/hierarchy/queries'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/lib/cn'
import { formatInteger } from '@/lib/format'
import { ConnectSourceWizard } from './ConnectSourceWizard'
import { DeleteConnectionDialog, EditConnectionDialog } from './ConnectionDialogs'
import { connectionPath } from './sections'

/**
 * IoT · Edge / Sensor Gateways — the account's connections, live.
 *
 * ── Four columns went away, and that is the honest outcome ────────────────
 * This table used to show Tags, Lag, Last sync and Status out of a hardcoded
 * constant. `SensorConnectionOut` returns `{id, name, plant_id,
 * sensor_connector_type_id, is_active}`, and there is no last-seen, no message
 * rate, no lag and no tag count anywhere in the resource. Rather than four
 * columns of em dashes — or four invented numbers an operator might act on —
 * they are gone until the API has something to put in them.
 *
 * What replaced them is real: the connector's own name and protocol family
 * resolved from the catalogue, and the site resolved from the hierarchy. Both are
 * joins the client has to perform, because the list carries ids only.
 *
 * So this screen reports how a connection is CONFIGURED and never whether it is
 * working. That is a gap in the resource rather than in the screen, and
 * "live · 0.1 s · 2,838 tags" beside a broker nobody has measured is the same
 * defect as a fabricated OEE. Recorded in BACKEND-GAPS.md §7 with what would
 * close it.
 *
 * ── The narrowing is the SERVER'S, all of it ──────────────────────────────
 * `list_connections` paginates at fifty and takes `plant_id`, `is_active`,
 * `search`, `sort_by` and `sort_dir`. This screen used to read the collection and
 * filter it in a `useMemo`, which filtered one page: the search missed anything
 * past row fifty, the summary called fifty rows the whole estate, and the site
 * filter offered only the sites that happened to land in the window. Every one of
 * those is now a request param. See `fetchConnections`.
 *
 * ── Two permissions, and they are not the same one ────────────────────────
 * `list_connections` accepts `sensors.manage_connections` OR
 * `sensors.manage_sensors`. Everything else here — the catalogue, create, the
 * detail read behind the edit dialog, update, delete and the probe — requires
 * `manage_connections` alone. So a `manage_sensors` holder gets the table and no
 * write controls, and the catalogue request must not fire for them at all: it
 * would 403, and the three reads share one error state.
 */

/** Filter sentinel. Radix rejects an item whose value is the empty string. */
const ALL = 'all'

const STATES = ['active', 'inactive']

/** From the footer's own ladder, and inside the server's `page_size` ceiling of 200. */
const DEFAULT_PAGE_SIZE = PAGE_SIZES.includes(50) ? 50 : PAGE_SIZES[0]

export function IotSensorsPage() {
  const { t } = useTranslation(['app', 'common'])
  useDocumentTitle(`${t('dataFusion.iot.title')} · ${t('common:brand.name')}`)

  /**
   * Two gates, because the API has two answers.
   *
   * Neither is the authorisation — every request is checked again server-side.
   * They are what keeps somebody from being shown a table that answers 403 to
   * every control on it, and what keeps the catalogue request from firing for a
   * reader who is not entitled to it.
   */
  const manageConnections = usePermission('sensors.manage_connections')
  const manageSensors = usePermission('sensors.manage_sensors')

  const gatePending = manageConnections.isPending || manageSensors.isPending
  const canRead = manageConnections.granted || manageSensors.granted

  return (
    <div className="px-8 py-6 max-xl:px-5.5">
      {gatePending ? (
        <SkeletonRows count={4} />
      ) : canRead ? (
        <Directory canManage={manageConnections.granted} />
      ) : (
        <>
          <header className="mb-5">
            <h1 className="m-0 text-headline">{t('dataFusion.iot.title')}</h1>
          </header>
          <EmptyState
            icon="lock"
            title={t('dataFusion.iot.forbidden')}
            body={t('dataFusion.iot.forbiddenBody')}
          />
        </>
      )}
    </div>
  )
}

function Directory({ canManage }) {
  const { t } = useTranslation(['app', 'common'])

  /**
   * The input is local and the request term is debounced off it.
   *
   * Driving the query straight off the keystroke would put a request behind every
   * character — and this search is the server's, so each one is a round trip
   * rather than a filter over an array already in hand.
   */
  const [text, setText] = useState('')
  const [search, setSearch] = useState('')
  const [state, setState] = useState(ALL)
  /* The site filter, as `PlaceFilter` speaks it — `{id, tier, name, trail}` —
     or null for every site. */
  const [site, setSite] = useState(null)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  useEffect(() => {
    if (text.trim() === search) return
    const timer = setTimeout(() => setSearch(text.trim()), 300)
    return () => clearTimeout(timer)
  }, [text, search])

  /**
   * The page, DERIVED against the narrowing it was set for.
   *
   * Stamped rather than reset from an effect — the pattern Users & Teams and
   * Sites & Hierarchy both settled on. Narrowing a list while standing on page
   * four would otherwise ask the server for a page that no longer exists, and
   * answer with an empty table indistinguishable from "no matches".
   */
  const [pageState, setPageState] = useState({ key: '', value: 1 })
  const filterKey = `${search}|${state}|${site?.id ?? ALL}|${pageSize}`
  const page = pageState.key === filterKey ? pageState.value : 1
  const setPage = (value) => setPageState({ key: filterKey, value })

  const params = useMemo(
    () => ({
      plantId: site?.id,
      // Tri-state: undefined omits the param and means "either".
      isActive: state === ALL ? undefined : state === 'active',
      search: search || undefined,
      page,
      pageSize,
    }),
    [site, state, search, page, pageSize],
  )

  const connections = useConnections(params)
  // Only fetched for a reader entitled to it — `list_connector_types` is behind
  // `manage_connections` alone, and a 403 here would fail the whole screen.
  const connectorTypes = useConnectorTypes({ enabled: canManage })
  /* The sites on THIS page of connections, named through the per-level detail
     reads — not the whole estate. See `usePlaceNodes`. */
  const siteTargets = useMemo(
    () => (connections.data?.items ?? []).map((connection) => ({ tier: 'site', id: connection.plantId })),
    [connections.data],
  )
  const hierarchy = usePlaceNodes(siteTargets)

  const [connecting, setConnecting] = useState(false)
  const [editing, setEditing] = useState(null)
  const [removing, setRemoving] = useState(null)

  const typeById = useMemo(
    () => new Map((connectorTypes.data?.items ?? []).map((type) => [type.id, type])),
    [connectorTypes.data],
  )

  const sites = useMemo(
    () => (hierarchy.data?.items ?? []).filter((node) => node.tier === 'site'),
    [hierarchy.data],
  )

  const siteById = useMemo(() => new Map(sites.map((node) => [node.id, node])), [sites])

  /**
   * Rows decorated with the two joins the list cannot carry.
   *
   * No sort: `sort_by=name&sort_dir=asc` is in the request, so the server has
   * already ordered them. Re-sorting here would order one page of fifty against
   * nothing and disagree with the page boundaries the server drew.
   */
  const rows = useMemo(
    () =>
      (connections.data?.items ?? []).map((connection) => ({
        connection,
        type: typeById.get(connection.connectorTypeId) ?? null,
        site: siteById.get(connection.plantId) ?? null,
      })),
    [connections.data, typeById, siteById],
  )

  const total = connections.data?.total ?? 0
  const isNarrowed = search.length > 0 || state !== ALL || site !== null
  // Paging holds the previous page on screen (see useConnections), so an in-flight
  // request is a dim rather than a skeleton — the rows under the pointer stay put.
  const settling = connections.isPlaceholderData

  /**
   * The catalogue is only in the gate when it was actually requested.
   *
   * For a `manage_sensors` reader it is disabled, and a disabled query sits at
   * `isPending` forever — folding that into the gate would hold the screen on a
   * skeleton that never resolves.
   */
  /* The site names are NOT in the gate: they are looked up per row once the rows
     are known, and fill in as they land. A failed lookup leaves the id. */
  const isPending = connections.isPending || (canManage && connectorTypes.isPending)
  const isError = connections.isError || connectorTypes.isError
  const error = connections.error ?? connectorTypes.error

  function retry() {
    connections.refetch()
    if (canManage) connectorTypes.refetch()
    hierarchy.refetch()
  }

  function clearFilters() {
    setText('')
    setSearch('')
    setState(ALL)
    setSite(null)
  }

  return (
    <>
      <header className="mb-5 flex flex-wrap items-start justify-between gap-x-5 gap-y-3">
        <div className="min-w-0">
          <h1 className="m-0 text-headline">{t('dataFusion.iot.title')}</h1>
          <p className="mt-2 mb-0 text-body-sm text-ink-dim">
            {isPending || isError ? (
              t('dataFusion.iot.subtitle')
            ) : (
              <Num>{t('dataFusion.iot.summary', { sources: total })}</Num>
            )}
          </p>
        </div>
        {canManage ? (
          <ForgeButton variant="primary" onClick={() => setConnecting(true)}>
            {t('dataFusion.iot.connectSource')}
          </ForgeButton>
        ) : null}
      </header>

      {isPending ? (
        <SkeletonRows count={4} />
      ) : isError ? (
        <ErrorState error={error} title={t('dataFusion.iot.loadFailed')} onRetry={retry} />
      ) : total === 0 && !isNarrowed ? (
        <EmptyState
          icon="plug"
          title={t('dataFusion.iot.empty')}
          body={t('dataFusion.iot.emptyBody')}
          action={
            canManage ? (
              <ForgeButton variant="primary" onClick={() => setConnecting(true)}>
                {t('dataFusion.iot.connectSource')}
              </ForgeButton>
            ) : null
          }
        />
      ) : (
        <>
          <Panel flush className="mb-3.5 px-3.5 py-2.5">
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-2">
              <div className="flex min-w-[15rem] flex-1 items-center gap-2 rounded-sm border border-hairline bg-card px-3 py-1.5 focus-within:border-forge max-sm:w-full max-sm:flex-none">
                <Icon name="search" size="sm" className="text-ink-mute" />
                <label className="sr-only" htmlFor="source-search">
                  {t('dataFusion.iot.searchLabel')}
                </label>
                <input
                  id="source-search"
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  placeholder={t('dataFusion.iot.searchPlaceholder')}
                  className="min-w-0 flex-1 border-0 bg-transparent text-body-sm text-ink outline-none placeholder:text-ink-dim"
                />
              </div>

              <ForgeSelect
                id="source-state"
                labelHidden
                size="sm"
                label={t('dataFusion.iot.filterByState')}
                value={state}
                onValueChange={setState}
                className="w-[11rem] shrink-0"
                options={[
                  { value: ALL, label: t('dataFusion.iot.allStates') },
                  ...STATES.map((value) => ({ value, label: t(`dataFusion.iot.state.${value}`) })),
                ]}
              />

              {/* Every site in the account, through the shared place picker —
                  the same `HierarchyPicker` dialog the anomaly list and every place
                  field use — limited to sites, which is the one level a connection
                  attaches at. Server-side, as before: `plant_id` on the list. */}
              <PlaceFilter
                id="source-site"
                value={site}
                onChange={setSite}
                allowTiers={SITE_ONLY}
                label={t('dataFusion.iot.filterBySite')}
                emptyLabel={t('dataFusion.iot.allSites')}
                title={t('dataFusion.iot.filterBySite')}
                note=""
              />

              <p className="m-0 ms-auto text-body-sm text-ink-dim">
                <Num>
                  {t('dataFusion.iot.matchCount', { total: formatInteger(total) })}
                </Num>
              </p>
            </div>
          </Panel>

          {total ? (
            <Panel flush className="overflow-hidden">
              <div className={cn('overflow-x-auto transition-opacity', settling && 'opacity-60')}>
                <table className="w-full border-collapse">
                  <caption className="sr-only">{t('dataFusion.iot.tableCaption')}</caption>
                  <thead>
                    <tr>
                      {['source', 'connector', 'protocol', 'site', 'state'].map((column) => (
                        <th
                          key={column}
                          scope="col"
                          className="eyebrow bg-sunken px-3.5 py-2.5 text-start whitespace-nowrap"
                        >
                          {t(`dataFusion.iot.columns.${column}`)}
                        </th>
                      ))}
                      {canManage ? (
                        <th scope="col" className="bg-sunken px-3.5 py-2.5">
                          <span className="sr-only">{t('dataFusion.iot.columns.actions')}</span>
                        </th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ connection, type, site: node }) => (
                      <tr
                        key={connection.id}
                        className="border-t border-subtle transition-colors hover:border-forge/30 hover:bg-sunken"
                      >
                        {/* The name is the way in. Every other cell on this row
                            describes how the source is configured; this one opens
                            what it actually carries, which is where an operator is
                            usually headed. The gear beside it still edits the
                            connection itself. */}
                        <td className="px-3.5 py-2.5 text-body-sm font-semibold">
                          <Link
                            to={connectionPath(connection.id)}
                            className="inline-flex items-center gap-1.5 text-ink transition-colors hover:text-on-forge"
                          >
                            <span className="min-w-0 truncate">{connection.name}</span>
                            <Icon
                              name="right"
                              size="xs"
                              className="shrink-0 text-ink-mute rtl:rotate-180"
                            />
                          </Link>
                        </td>
                        <td className="px-3.5 py-2.5 text-body-sm text-ink-dim">
                          {type ? (
                            <span translate="no">{type.name}</span>
                          ) : canManage ? (
                            /* A connector type this build has never seen. The
                               backend can seed one without a frontend release, so
                               the id is stated rather than the cell left blank. */
                            <Mono>{connection.connectorTypeId}</Mono>
                          ) : (
                            /* Not unknown — unreadable. The catalogue is behind a
                               permission this reader does not hold, so naming the
                               id would report a gap in the data where there is a
                               gap in access. */
                            <span className="text-ink-mute">
                              {t('dataFusion.iot.catalogueHidden')}
                            </span>
                          )}
                        </td>
                        <td className="px-3.5 py-2.5">
                          {type ? (
                            <Mono className="text-ink-dim uppercase">{type.protocolFamily}</Mono>
                          ) : (
                            <Dash />
                          )}
                        </td>
                        <td className="px-3.5 py-2.5 text-body-sm text-ink-dim">
                          {node ? (
                            node.name
                          ) : hierarchy.resolving.has(connection.plantId) ? (
                            <Dash />
                          ) : (
                            /* Deletion is soft everywhere in this API and nothing
                               cascades, so a connection outlives the plant it was
                               registered against and lands here. */
                            <span className="flex min-w-0 flex-col gap-0.5">
                              <Mono className="truncate">{connection.plantId}</Mono>
                              <span className="text-micro font-normal text-on-amber">
                                {t('dataFusion.iot.siteUnresolved')}
                              </span>
                            </span>
                          )}
                        </td>
                        {/* Both states named. The exception-only pattern read as
                            a bug: an active connection had an empty cell, and a
                            reader had to know to interpret the absence as
                            "healthy". Naming both leaves nothing to infer. */}
                        <td className="px-3.5 py-2.5">
                          {connection.isActive ? (
                            <span className="inline-flex items-center gap-1.5 rounded-pill tint-green px-2.5 py-[3px] text-micro whitespace-normal text-on-green">
                              {t('dataFusion.iot.state.active')}
                            </span>
                          ) : (
                            <PlainBadge>{t('dataFusion.iot.state.inactive')}</PlainBadge>
                          )}
                        </td>
                        {canManage ? (
                          <td className="px-3.5 py-2.5">
                            <span className="flex items-center justify-end gap-1">
                              <IconButton
                                bare
                                label={t('dataFusion.iot.configure', { name: connection.name })}
                                onClick={() => setEditing(connection)}
                                className="size-8"
                              >
                                <Icon name="gear" size="sm" />
                              </IconButton>
                              <IconButton
                                bare
                                label={t('dataFusion.iot.remove', { name: connection.name })}
                                onClick={() => setRemoving(connection)}
                                className="size-8 hover:text-on-red"
                              >
                                <Icon name="trash" size="sm" />
                              </IconButton>
                            </span>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <TablePagination
                total={total}
                page={page}
                pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                label={t('dataFusion.iot.rowNoun')}
              />
            </Panel>
          ) : (
            <EmptyState
              icon="search"
              title={t('dataFusion.iot.noMatch')}
              body={t('dataFusion.iot.noMatchBody')}
              action={
                isNarrowed ? (
                  <ForgeButton onClick={clearFilters}>
                    {t('common:actions.clearFilters')}
                  </ForgeButton>
                ) : null
              }
            />
          )}
        </>
      )}

      {/* Every one of these is behind `manage_connections` server-side, so none of
          them is mounted for a reader who only holds `manage_sensors` — there is no
          control that opens them either. */}
      {canManage ? (
        <>
          <ConnectSourceWizard open={connecting} onClose={() => setConnecting(false)} />
          <EditConnectionDialog connection={editing} onClose={() => setEditing(null)} />
          <DeleteConnectionDialog connection={removing} onClose={() => setRemoving(null)} />
        </>
      ) : null}
    </>
  )
}

/** A value the source does not report. Never an empty cell — a blank reads as a bug. */
function Dash() {
  return <span className="text-ink-mute">—</span>
}

/** A connection attaches to a plant, so the filter offers plants alone. */
const SITE_ONLY = new Set(['site'])
